import type { AppDispatch } from '@/app/store/store';
import { plpApi } from '@/features/plp/infra/adapters/plpListingApi';
import type { PlpListingRequestArgs } from '@/features/plp/infra/adapters/plpListingApi';
import type { FiltersRepository, FetchFiltersParams, FetchFiltersOptions } from '../../domain/interfaces/FiltersRepository';
import type { PlpFiltersResult } from '../../domain/models/Filter';
import { adaptFiltersResponseDto } from '../mappers/filters.mapper';
import type { PlpFiltersResponseDTO } from '../dto/filters.dto';

async function dispatchQuery<T>(
  dispatch: AppDispatch,
  initiateResult: unknown,
  signal?: AbortSignal
): Promise<T> {
  const pendingQuery = dispatch(
    initiateResult as unknown as Parameters<AppDispatch>[0]
  ) as unknown as { unwrap: () => Promise<T>; abort: () => void };

  const onAbort = () => {
    pendingQuery.abort();
  };
  if (signal) {
    if (signal.aborted) {
      onAbort();
    } else {
      signal.addEventListener('abort', onAbort);
    }
  }
  try {
    return await pendingQuery.unwrap();
  } finally {
    if (signal) {
      signal.removeEventListener('abort', onAbort);
    }
  }
}

function isAbortError(error: unknown, signal?: AbortSignal): boolean {
  return (
    Boolean(signal?.aborted) ||
    (error instanceof DOMException && error.name === 'AbortError') ||
    (typeof error === 'object' &&
      error !== null &&
      'name' in error &&
      (error as { name: string }).name === 'AbortError')
  );
}

export class FiltersAdapter implements FiltersRepository {
  constructor(private readonly dispatch: AppDispatch) {}

  async fetchFilters(params: FetchFiltersParams, options?: FetchFiltersOptions): Promise<PlpFiltersResult> {
    const signal = options?.signal;
    try {
      const dto = await dispatchQuery<PlpFiltersResponseDTO>(
        this.dispatch,
        plpApi.endpoints.getListingFilters.initiate({
          ...params,
          is_for_filter_change: params.is_for_filter_change ?? true,
        } as PlpListingRequestArgs),
        signal
      );
      return adaptFiltersResponseDto(dto ?? {});
    } catch (error) {
      if (isAbortError(error, signal)) {
        throw new DOMException('Aborted', 'AbortError');
      }
      console.error('Error fetching PLP filters:', error);
      return { filters: [] };
    }
  }
}

